import { TaskProgressRo, TaskRo } from 'src/app/services/api.generated';
import { api } from 'src/app/services/api';
import type { AppDispatch } from 'src/app/store';

export type TaskWithProcessingProgress = TaskRo & {
  processingProgress?: number;
  processingLabel?: string;
};

export const ACTIVE_TASK_STATES = new Set<TaskRo['state']>([
  'CREATE_IMAGES',
  'CREATING_IMAGES',
  'PENDING',
  'PROCESSING',
  'PREPARING',
  'PARTIAL_READY',
]);

const TASK_STATE_SET = new Set<TaskRo['state']>([
  'CREATE_IMAGES',
  'CREATING_IMAGES',
  'PENDING',
  'PROCESSING',
  'PREPARING',
  'PARTIAL_READY',
  'READY',
  'READY_WITH_ERRORS',
  'ERROR',
]);

export function isActiveTaskState(state: TaskRo['state']) {
  return ACTIVE_TASK_STATES.has(state);
}

export function isFinishedTaskState(state: TaskRo['state'] | undefined) {
  return state === 'READY' || state === 'READY_WITH_ERRORS' || state === 'ERROR';
}

export function isOpenableTaskState(state: TaskRo['state'] | undefined) {
  return state === 'READY' || state === 'READY_WITH_ERRORS';
}

export function resolveTaskProgressState(
  progress: Pick<TaskProgressRo, 'stage'> & { state?: TaskRo['state'] }
): TaskRo['state'] | undefined {
  // Pages can finish (stage=done, percent=100) a tick before task.state flips to READY.
  // Prefer terminal stages so the UI does not stick at 99% with a spinning "complete" label.
  const fromStage = taskStateFromStage(progress.stage);
  if (fromStage && isFinishedTaskState(fromStage)) {
    return fromStage;
  }
  if (progress.state && TASK_STATE_SET.has(progress.state)) {
    return progress.state;
  }
  return fromStage;
}

/** Initial poll interval while waiting for output generation. */
export const OUTPUT_STATE_POLL_INTERVAL_MS = 4000;
/** After this elapsed time, poll less often so large docs do not hammer the API. */
export const OUTPUT_STATE_POLL_BACKOFF_AFTER_MS = 90_000;
/** Poll interval after backoff (and while the tab is hidden). */
export const OUTPUT_STATE_POLL_BACKOFF_INTERVAL_MS = 15_000;
/** Soft UI copy only; never used as a stop condition. */
export const OUTPUT_STATE_STILL_GENERATING_AFTER_MS = 120_000;

export function isGeneratingOutputState(outputState: TaskRo['outputState'] | undefined) {
  return outputState === 'GENERATING_OUTPUT' || outputState === 'GENERATE_OUTPUT';
}

/**
 * Success, failure, idle, or outdated (the redactions changed while it ran);
 * worker failure currently resets to NOT_GENERATED.
 */
export function isTerminalOutputState(outputState: TaskRo['outputState'] | undefined) {
  return (
    outputState === 'GENERATED_OUTPUT' ||
    outputState === 'OUTDATED_OUTPUT' ||
    outputState === 'ERROR' ||
    outputState === 'NOT_GENERATED'
  );
}

export function getOutputStatePollDelayMs(startedAt: number, isHidden = document.hidden) {
  if (isHidden || Date.now() - startedAt >= OUTPUT_STATE_POLL_BACKOFF_AFTER_MS) {
    return OUTPUT_STATE_POLL_BACKOFF_INTERVAL_MS;
  }
  return OUTPUT_STATE_POLL_INTERVAL_MS;
}

/**
 * Polls until `poll` returns false (terminal state) or the cleanup function runs.
 * No attempt cap; backs off after OUTPUT_STATE_POLL_BACKOFF_AFTER_MS.
 */
export function startOutputStatePolling(poll: () => Promise<boolean>): () => void {
  let cancelled = false;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const startedAt = Date.now();

  const clearTimer = () => {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
      timeoutId = undefined;
    }
  };

  const schedule = (delayMs: number) => {
    clearTimer();
    if (cancelled) return;
    timeoutId = setTimeout(() => {
      void run();
    }, delayMs);
  };

  const run = async () => {
    if (cancelled) return;

    if (document.hidden) {
      schedule(OUTPUT_STATE_POLL_BACKOFF_INTERVAL_MS);
      return;
    }

    let shouldContinue = true;
    try {
      shouldContinue = await poll();
    } catch {
      shouldContinue = true;
    }

    if (cancelled || !shouldContinue) return;
    schedule(getOutputStatePollDelayMs(startedAt));
  };

  const onVisibilityChange = () => {
    if (cancelled || document.hidden) return;
    schedule(0);
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  void run();

  return () => {
    cancelled = true;
    clearTimer();
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
}

export type WorkspaceFolderQueryKey =
  | { endpoint: 'workspacesControllerGetRootFolder'; args: { id: string } }
  | {
      endpoint: 'workspacesControllerGetFolderById';
      args: { workspaceId: string; folderId: string };
    };

export function patchTaskOutputState(
  dispatch: AppDispatch,
  folderQueryKey: WorkspaceFolderQueryKey,
  taskId: string,
  outputState: TaskRo['outputState'],
  stuckStage?: TaskRo['stuckStage'] | null
) {
  dispatch(
    api.util.updateQueryData(folderQueryKey.endpoint, folderQueryKey.args, (draft) => {
      const task = draft.tasks.find((item) => item.id === taskId);
      if (task) {
        task.outputState = outputState;
        if (stuckStage !== undefined) {
          task.stuckStage = stuckStage ?? undefined;
        }
      }
    })
  );
}

export function taskStateFromStage(stage: string): TaskRo['state'] | undefined {
  const normalizedStage = stage.toUpperCase();
  if (normalizedStage === 'DONE') {
    return 'READY';
  }
  if (normalizedStage === 'IMAGE') {
    return 'CREATING_IMAGES';
  }
  if (normalizedStage === 'AI') {
    return 'PROCESSING';
  }
  if (normalizedStage === 'ERROR' || normalizedStage === 'FAILED') {
    return 'READY_WITH_ERRORS';
  }
  return TASK_STATE_SET.has(normalizedStage as TaskRo['state'])
    ? (normalizedStage as TaskRo['state'])
    : undefined;
}

export function getApiBaseUrl() {
  const baseUrl =
    process.env.NODE_ENV === 'development'
      ? process.env.REACT_APP_DEVELOPMENT_BASE_URL
      : process.env.REACT_APP_PRODUCTION_BASE_URL;

  if (!baseUrl || baseUrl === 'auto') {
    return window.location.origin;
  }

  return baseUrl;
}

export function progressFromStreamMessage(message: string): TaskProgressRo | undefined {
  const dataLines = message
    .split(/\r?\n/)
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.replace(/^data:\s?/, ''));

  if (dataLines.length === 0 && message.trim().startsWith(':')) {
    return undefined;
  }

  const payload = dataLines.length > 0 ? dataLines.join('\n') : message.trim();
  if (!payload || payload === '[DONE]') {
    return undefined;
  }

  try {
    return JSON.parse(payload) as TaskProgressRo;
  } catch {
    return undefined;
  }
}

function progressMessagesFromBuffer(buffer: string) {
  const progressItems: TaskProgressRo[] = [];
  let remaining = buffer;

  if (/\r?\n\r?\n/.test(buffer)) {
    const messages = buffer.split(/\r?\n\r?\n/);
    remaining = messages.pop() ?? '';
    messages.forEach((message) => {
      const progress = progressFromStreamMessage(message);
      if (progress) {
        progressItems.push(progress);
      }
    });
    return { progressItems, remaining };
  }

  const lines = buffer.split(/\r?\n/);
  remaining = lines.pop() ?? '';
  lines.forEach((line) => {
    const progress = progressFromStreamMessage(line);
    if (progress) {
      progressItems.push(progress);
    }
  });

  return { progressItems, remaining };
}

/** A progress request answered with an HTTP error; carries the status. */
export class TaskProgressHttpError extends Error {
  readonly status: number;

  constructor(source: 'stream' | 'poll', status: number) {
    super(`Task progress ${source} failed with status ${status}`);
    this.name = 'TaskProgressHttpError';
    this.status = status;
  }
}

/** Retrying cannot help: the task is gone, not ours, or the session is. */
export type TaskProgressStopReason = 'not-found' | 'forbidden' | 'unauthorized';

export type TaskProgressWatchOutcome = {
  stopped: 'finished' | 'aborted' | TaskProgressStopReason;
};

const STOP_REASONS: Record<number, TaskProgressStopReason> = {
  401: 'unauthorized',
  403: 'forbidden',
  404: 'not-found',
};

/**
 * The stop reason of a 401, 403 or 404; undefined for anything worth retrying.
 * A 401 only ends a watch when the session re-check says so (see sessionRecheck).
 */
export function taskProgressStopReason(error: unknown): TaskProgressStopReason | undefined {
  // Checked by name and field, not instanceof: transpiled Error subclasses may lose it.
  const { name, status } = (error ?? {}) as { name?: unknown; status?: unknown };
  if (name !== 'TaskProgressHttpError' || typeof status !== 'number') return undefined;
  return STOP_REASONS[status];
}

/**
 * What a watcher does after a 401 and the session re-check: 'stop' when the
 * session is gone (logout) or a new token arrived (the caller's effect
 * restarts with it); 'continue' with the normal backoff when the token stays
 * the same (refresh 403, "the 401 was a blip") or the check itself failed.
 */
export type SessionRecheckVerdict = 'stop' | 'continue';

/**
 * A 'continue' verdict is reused this long (the longest reconnect backoff), so
 * retries do not ask for a refresh again on every step.
 */
export const SESSION_RECHECK_REUSE_MS = 15_000;

/**
 * For the raw progress fetches (not through baseQuery) that got 401: the same
 * session re-check as baseQuery (refresh, or its logout when the session is
 * gone). Share one callback between all watchers of an effect: concurrent
 * calls await one re-check, and a 'continue' verdict is reused for
 * SESSION_RECHECK_REUSE_MS. recheckSession also shares one in-flight re-check
 * per token across effects, so many watchers cause one refresh call.
 */
export function sessionRecheck(
  dispatch: (action: any) => unknown,
  failedToken: string | undefined
): () => Promise<SessionRecheckVerdict> {
  let last: { promise: Promise<SessionRecheckVerdict>; settledAt?: number; verdict?: SessionRecheckVerdict } | null =
    null;
  return () => {
    // Without a token there is no session to re-check.
    if (!failedToken) return Promise.resolve('stop');
    if (
      last &&
      (last.settledAt === undefined ||
        last.verdict === 'stop' ||
        Date.now() - last.settledAt < SESSION_RECHECK_REUSE_MS)
    ) {
      return last.promise;
    }
    const entry: NonNullable<typeof last> = {
      promise: Promise.resolve(
        dispatch(async (innerDispatch: (action: unknown) => unknown, getState: () => unknown) => {
          // Loaded on use: baseQuery pulls in i18n, which list tests mock away.
          const [{ recheckSession }, { createSessionHooks }] = await Promise.all([
            import('src/app/services/authSession'),
            import('src/app/services/baseQuery'),
          ]);
          return recheckSession(failedToken, createSessionHooks(getState, innerDispatch));
        })
      )
        .then((result): SessionRecheckVerdict => {
          const recheck = result as { kind?: unknown; token?: unknown } | undefined;
          if (recheck?.kind === 'logout') return 'stop';
          if (recheck?.kind === 'retry' && recheck.token !== failedToken) return 'stop';
          return 'continue';
        })
        .catch((): SessionRecheckVerdict => 'continue')
        .then((verdict) => {
          entry.verdict = verdict;
          entry.settledAt = Date.now();
          return verdict;
        }),
    };
    last = entry;
    return entry.promise;
  };
}

export async function readTaskProgressStream({
  id,
  authToken,
  signal,
  onProgress,
}: {
  id: string;
  authToken?: string;
  signal: AbortSignal;
  onProgress: (progress: TaskProgressRo) => void;
}) {
  const response = await fetch(`${getApiBaseUrl()}/api/v1/tasks/${encodeURIComponent(id)}/progress/stream`, {
    headers: {
      ...(authToken && { authorization: `Bearer ${authToken}` }),
    },
    signal,
  });

  if (!response.ok) {
    throw new TaskProgressHttpError('stream', response.status);
  }

  if (!response.body) {
    onProgress((await response.json()) as TaskProgressRo);
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (!signal.aborted) {
    const { value, done } = await reader.read();
    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const { progressItems, remaining } = progressMessagesFromBuffer(buffer);
    buffer = remaining;
    progressItems.forEach(onProgress);
  }

  buffer += decoder.decode();
  const { progressItems } = progressMessagesFromBuffer(`${buffer}\n`);
  progressItems.forEach(onProgress);
}

/** Initial SSE reconnect delay after a dropped stream. */
export const PROGRESS_RECONNECT_BASE_MS = 1500;
/** Cap reconnect backoff so large docs keep retrying. */
export const PROGRESS_RECONNECT_MAX_MS = 15_000;
/** Fallback POST /progress poll while streaming (or after stream death). */
export const PROGRESS_POLL_INTERVAL_MS = 4000;
/** After this elapsed time, poll less often. */
export const PROGRESS_POLL_BACKOFF_AFTER_MS = 90_000;
/** Poll interval after backoff (and while the tab is hidden). */
export const PROGRESS_POLL_BACKOFF_INTERVAL_MS = 15_000;

export function isTerminalTaskProgress(progress: TaskProgressRo) {
  if (progress.percent >= 100 || progress.stage === 'error' || progress.stage === 'done') {
    return true;
  }
  const state = resolveTaskProgressState(progress);
  return state !== undefined && isFinishedTaskState(state);
}

function getProgressPollDelayMs(startedAt: number, isHidden = document.hidden) {
  if (isHidden || Date.now() - startedAt >= PROGRESS_POLL_BACKOFF_AFTER_MS) {
    return PROGRESS_POLL_BACKOFF_INTERVAL_MS;
  }
  return PROGRESS_POLL_INTERVAL_MS;
}

function sleep(ms: number, signal: AbortSignal) {
  return new Promise<void>((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const timeoutId = setTimeout(resolve, ms);
    const onAbort = () => {
      clearTimeout(timeoutId);
      resolve();
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

export async function fetchTaskProgressOnce({
  id,
  authToken,
  signal,
}: {
  id: string;
  authToken?: string;
  signal?: AbortSignal;
}): Promise<TaskProgressRo> {
  const response = await fetch(
    `${getApiBaseUrl()}/api/v1/tasks/${encodeURIComponent(id)}/progress`,
    {
      method: 'POST',
      headers: {
        ...(authToken && { authorization: `Bearer ${authToken}` }),
      },
      signal,
    }
  );

  if (!response.ok) {
    throw new TaskProgressHttpError('poll', response.status);
  }

  return (await response.json()) as TaskProgressRo;
}

/**
 * Keep task progress alive for long jobs: SSE with reconnect + POST poll fallback.
 * Stops when progress is terminal or `signal` aborts. Never attempt-caps, but
 * a 403 or 404 from the stream or the poll ends it at once with that reason.
 * A 401 runs onUnauthorized (the session re-check) and ends the watch only
 * when it answers 'stop'; otherwise, like 5xx, 408, 429 and network errors,
 * the backoff goes on.
 */
export async function watchTaskProgress({
  id,
  authToken,
  signal: outerSignal,
  onProgress,
  onUnauthorized,
}: {
  id: string;
  authToken?: string;
  signal: AbortSignal;
  onProgress: (progress: TaskProgressRo) => void;
  onUnauthorized?: () => Promise<SessionRecheckVerdict>;
}): Promise<TaskProgressWatchOutcome> {
  let finished = false;
  let stopped: TaskProgressStopReason | undefined;
  const startedAt = Date.now();
  let reconnectAttempt = 0;
  // Aborted by the caller, or by a stop: ends the open stream and the sleep.
  const inner = new AbortController();
  const onOuterAbort = () => inner.abort();
  if (outerSignal.aborted) inner.abort();
  outerSignal.addEventListener('abort', onOuterAbort, { once: true });
  const signal = inner.signal;

  const stop = (reason: TaskProgressStopReason) => {
    if (!stopped) {
      stopped = reason;
      inner.abort();
    }
  };

  /**
   * True when the error ends the watch: a 403 or 404, or a 401 after which
   * the session re-check says stop. Waits for that re-check.
   */
  const stopOn = async (error: unknown) => {
    const reason = taskProgressStopReason(error);
    if (!reason) return false;
    if (reason === 'unauthorized') {
      const verdict = onUnauthorized ? await onUnauthorized() : 'continue';
      if (verdict === 'continue') return false;
    }
    stop(reason);
    return true;
  };

  const emit = (progress: TaskProgressRo) => {
    onProgress(progress);
    if (isTerminalTaskProgress(progress)) {
      finished = true;
    }
  };

  const pollOnce = async () => {
    if (signal.aborted || finished) {
      return;
    }
    try {
      emit(await fetchTaskProgressOnce({ id, authToken, signal }));
    } catch (error) {
      if (signal.aborted || (await stopOn(error)) || signal.aborted) {
        return;
      }
      console.error('Task progress poll failed', error);
    }
  };

  let pollTimeoutId: ReturnType<typeof setTimeout> | undefined;
  const clearPollTimer = () => {
    if (pollTimeoutId !== undefined) {
      clearTimeout(pollTimeoutId);
      pollTimeoutId = undefined;
    }
  };

  const schedulePoll = (delayMs: number) => {
    clearPollTimer();
    if (signal.aborted || finished) {
      return;
    }
    pollTimeoutId = setTimeout(() => {
      void (async () => {
        if (document.hidden) {
          schedulePoll(PROGRESS_POLL_BACKOFF_INTERVAL_MS);
          return;
        }
        await pollOnce();
        if (!signal.aborted && !finished) {
          schedulePoll(getProgressPollDelayMs(startedAt));
        }
      })();
    }, delayMs);
  };

  const onVisibilityChange = () => {
    if (signal.aborted || finished || document.hidden) {
      return;
    }
    schedulePoll(0);
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  // Immediate poll so a dead SSE still shows current state within one tick.
  schedulePoll(0);

  try {
    while (!signal.aborted && !finished) {
      try {
        await readTaskProgressStream({
          id,
          authToken,
          signal,
          onProgress: emit,
        });
        reconnectAttempt = 0;
      } catch (error) {
        if (signal.aborted || (await stopOn(error)) || signal.aborted) {
          break;
        }
        console.error('Task progress stream failed', error);
      }

      if (signal.aborted || finished) {
        break;
      }

      // Stream ended without a terminal event (proxy timeout, network blip).
      await pollOnce();
      if (signal.aborted || finished) {
        break;
      }

      const delay = Math.min(
        PROGRESS_RECONNECT_MAX_MS,
        PROGRESS_RECONNECT_BASE_MS * 2 ** Math.min(reconnectAttempt, 4)
      );
      reconnectAttempt += 1;
      await sleep(delay, signal);
    }
  } finally {
    clearPollTimer();
    document.removeEventListener('visibilitychange', onVisibilityChange);
    outerSignal.removeEventListener('abort', onOuterAbort);
  }
  return { stopped: stopped ?? (finished ? 'finished' : 'aborted') };
}
